import React, { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Flag } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Per-hole scorecard for a golf event. Auto-computes Stableford points based on
 * the GolfConfig the organizer set at event creation. Saves on every change
 * (debounced) so the leaderboard stays live.
 */

const STANDARD_PARS_18 = [4, 4, 3, 5, 4, 4, 3, 5, 4, 4, 3, 5, 4, 4, 3, 5, 4, 4]; // typical 72 par
const STANDARD_PARS_9 = STANDARD_PARS_18.slice(0, 9);

export default function GolfScorecard({ event, eventKind = "match", onSaved }) {
  const { user } = useAuth();
  const cfg = event?.golf || {};
  const numHoles = cfg.holes || 18;
  const fmt = cfg.format || "stroke";
  const allowance = cfg.handicap_allowance ?? 1.0;
  const coursePar = cfg.course_par || 72;

  // Local card state
  const [holes, setHoles] = useState(() => seedHoles(numHoles));
  const [hcpIndex, setHcpIndex] = useState("");
  const [courseHcp, setCourseHcp] = useState("");
  const [teamId, setTeamId] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Hydrate existing card on mount
  useEffect(() => {
    if (!event?.id || !user) { setLoading(false); return; }
    api.get(`/golf/scorecards?event_id=${event.id}`)
      .then(({ data }) => {
        if (data?.holes && data.holes.length > 0) {
          // Backfill in case organizer changed numHoles
          const padded = seedHoles(numHoles).map((h) => {
            const found = data.holes.find((x) => x.hole === h.hole);
            return found ? { ...h, ...found } : h;
          });
          setHoles(padded);
          setHcpIndex(data.handicap_index ?? "");
          setCourseHcp(data.course_handicap ?? "");
          setTeamId(data.team_id ?? "");
        }
      })
      .catch(() => { /* fresh card */ })
      .finally(() => setLoading(false));
  }, [event?.id, user, numHoles]);

  const totals = useMemo(() => computeTotals(holes), [holes]);

  const setStrokes = (idx, v) => {
    const n = v === "" ? null : Math.max(1, Math.min(15, parseInt(v, 10) || 0));
    setHoles((arr) => arr.map((h, i) => i === idx ? { ...h, strokes: n } : h));
  };
  const setPar = (idx, v) => {
    const n = Math.max(3, Math.min(6, parseInt(v, 10) || 4));
    setHoles((arr) => arr.map((h, i) => i === idx ? { ...h, par: n } : h));
  };

  const save = useCallback(async () => {
    if (!event?.id) return;
    setSaving(true);
    try {
      await api.post("/golf/scorecards", {
        event_id: event.id,
        event_kind: eventKind,
        handicap_index: hcpIndex === "" ? null : Number(hcpIndex),
        course_handicap: courseHcp === "" ? null : Number(courseHcp),
        team_id: teamId || null,
        holes: holes.filter((h) => h.strokes != null).map((h) => ({
          hole: h.hole, par: h.par, strokes: h.strokes, hcp_strokes: h.hcp_strokes || 0,
        })),
      });
      toast.success("Scorecard saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  }, [event?.id, eventKind, hcpIndex, courseHcp, teamId, holes, onSaved]);

  const reset = async () => {
    if (!window.confirm("Reset your scorecard for this event?")) return;
    try {
      await api.delete(`/golf/scorecards/${event.id}`);
      setHoles(seedHoles(numHoles));
      setHcpIndex("");
      setCourseHcp("");
      toast.success("Scorecard reset");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (loading) {
    return <div className="pp-card p-6 text-center text-zinc-500 text-sm">Loading scorecard…</div>;
  }

  return (
    <section className="pp-card p-4" data-testid="golf-scorecard">
      <header className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Flag size={18} className="text-[#00E676]" />
          <h3 className="font-heading text-lg font-black">Your scorecard</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00E676]/10 text-[#00E676] uppercase tracking-wider">{fmt.replace(/_/g, " ")}</span>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="gs-hcp-idx" placeholder="Index (e.g. 14.2)" value={hcpIndex}
            onChange={(e) => setHcpIndex(e.target.value)} className="pp-input text-xs w-32" />
          <input data-testid="gs-course-hcp" placeholder="Course hcp" value={courseHcp}
            onChange={(e) => setCourseHcp(e.target.value)} className="pp-input text-xs w-28" />
          {(cfg.team_size || 1) > 1 && (
            <input data-testid="gs-team-id" placeholder="Team A/B/…" value={teamId}
              onChange={(e) => setTeamId(e.target.value)} className="pp-input text-xs w-24" />
          )}
        </div>
      </header>

      <div className="overflow-x-auto" data-testid="gs-table-wrap">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
              <th className="p-2 text-left">Hole</th>
              <th className="p-2 text-center">Par</th>
              <th className="p-2 text-center">Strokes</th>
              <th className="p-2 text-center">vs Par</th>
              {fmt === "stableford" && <th className="p-2 text-center">Pts</th>}
            </tr>
          </thead>
          <tbody>
            {holes.map((h, idx) => {
              const overPar = h.strokes ? h.strokes - h.par : null;
              const pts = h.strokes ? Math.max(0, 2 - (h.strokes - h.par - (h.hcp_strokes || 0))) : 0;
              const overParColor = overPar == null ? "" : overPar < 0 ? "text-[#00E676]" : overPar > 0 ? "text-[#FF3B30]" : "text-zinc-300";
              return (
                <tr key={h.hole} data-testid={`gs-row-${h.hole}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="p-1.5 text-left text-zinc-400 w-12">{h.hole}</td>
                  <td className="p-1.5 text-center">
                    <input type="number" min={3} max={6} value={h.par} data-testid={`gs-par-${h.hole}`}
                      onChange={(e) => setPar(idx, e.target.value)}
                      className="pp-input w-12 text-center text-xs py-1" />
                  </td>
                  <td className="p-1.5 text-center">
                    <input type="number" min={1} max={15} value={h.strokes ?? ""} data-testid={`gs-strokes-${h.hole}`}
                      onChange={(e) => setStrokes(idx, e.target.value)}
                      className="pp-input w-14 text-center text-sm py-1 font-bold" />
                  </td>
                  <td className={`p-1.5 text-center font-bold ${overParColor}`} data-testid={`gs-diff-${h.hole}`}>
                    {overPar == null ? "—" : overPar > 0 ? `+${overPar}` : overPar === 0 ? "E" : overPar}
                  </td>
                  {fmt === "stableford" && (
                    <td className="p-1.5 text-center text-zinc-300" data-testid={`gs-pts-${h.hole}`}>{h.strokes ? pts : "—"}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-white/10 font-black">
              <td className="p-2 text-left text-zinc-300">Total</td>
              <td className="p-2 text-center text-zinc-400">{totals.par}</td>
              <td className="p-2 text-center text-[#00E676]" data-testid="gs-total-strokes">{totals.strokes || "—"}</td>
              <td className={`p-2 text-center ${totals.over === 0 ? "text-zinc-300" : totals.over < 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`} data-testid="gs-total-diff">
                {totals.strokes === 0 ? "—" : totals.over > 0 ? `+${totals.over}` : totals.over === 0 ? "E" : totals.over}
              </td>
              {fmt === "stableford" && (
                <td className="p-2 text-center text-[#00E676]" data-testid="gs-total-pts">{totals.points}</td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5 text-xs text-zinc-500">
        <span>Course par {coursePar} · {numHoles} holes · Allowance {Math.round(allowance * 100)}%</span>
        <div className="flex gap-2">
          <button onClick={reset} className="pp-btn-secondary px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid="gs-reset">
            <RotateCcw size={12} /> Reset
          </button>
          <button onClick={save} disabled={saving} className="pp-btn-primary px-4 py-1.5 rounded-lg text-xs flex items-center gap-1 disabled:opacity-40" data-testid="gs-save">
            <Save size={12} /> {saving ? "Saving…" : "Save scorecard"}
          </button>
        </div>
      </div>
    </section>
  );
}

function seedHoles(n) {
  const pars = n === 9 ? STANDARD_PARS_9 : n === 18 ? STANDARD_PARS_18 : Array.from({ length: n }, () => 4);
  return Array.from({ length: n }, (_, i) => ({
    hole: i + 1,
    par: pars[i] || 4,
    strokes: null,
    hcp_strokes: 0,
  }));
}

function computeTotals(holes) {
  let strokes = 0;
  let par = 0;
  let points = 0;
  for (const h of holes) {
    if (h.strokes == null) continue;
    strokes += h.strokes;
    par += h.par;
    points += Math.max(0, 2 - (h.strokes - h.par - (h.hcp_strokes || 0)));
  }
  return { strokes, par, over: strokes - par, points };
}
